import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Spin,
  Switch,
  TimePicker,
  Typography,
  message as toast,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import PageTitle from 'components/page-title';
import FileUpload from 'components/file-upload';
import { UPLOAD_TYPE } from 'constants/common';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { SupportConfigView } from 'pages/support-shared/types';
import { supportError, useSupportMeta } from 'pages/support-shared/utils';
import { PersonAvatar } from 'pages/support-shared/components';

const DAYS = [
  { value: 1, label: 'T2' },
  { value: 2, label: 'T3' },
  { value: 3, label: 'T4' },
  { value: 4, label: 'T5' },
  { value: 5, label: 'T6' },
  { value: 6, label: 'T7' },
  { value: 7, label: 'CN' },
];

interface FormValue {
  welcomeMessage: string;
  workDays: number[];
  workStart: Dayjs;
  workEnd: Dayjs;
  autoReplyEnabled: boolean;
  autoReplyMessage: string;
  autoReplyCooldownMinutes: number;
  signAgentName: boolean;
}

/** Cài đặt kênh hỗ trợ — chỉ ADMIN (backend chặn MOD bằng 400 SUPPORT_ADMIN_ONLY). */
const SettingsTab = () => {
  const meta = useSupportMeta();
  const [config, setConfig] = useState<SupportConfigView | null>(null);
  const [nickName, setNickName] = useState('HP Media Hỗ trợ');
  const [avatar, setAvatar] = useState<{ fileKey: string; fileUrl: string } | undefined>();
  const [savingAccount, setSavingAccount] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<FormValue>();

  const apply = (data: SupportConfigView) => {
    setConfig(data);
    setNickName(data.account?.nickName || 'HP Media Hỗ trợ');
    const key = data.account?.avatar || '';
    setAvatar(key ? { fileKey: key, fileUrl: /^https?:/i.test(key) ? key : `${SERVER_FILE_ENDPOINT}/${key}` } : undefined);
    form.setFieldsValue({
      welcomeMessage: data.welcomeMessage,
      workDays: data.workDays.split(',').map(Number),
      workStart: dayjs(data.workStart, 'HH:mm'),
      workEnd: dayjs(data.workEnd, 'HH:mm'),
      autoReplyEnabled: data.autoReplyEnabled,
      autoReplyMessage: data.autoReplyMessage,
      autoReplyCooldownMinutes: data.autoReplyCooldownMinutes,
      signAgentName: data.signAgentName,
    });
  };

  useEffect(() => {
    AxiosUtils.get(API.supportConfig)
      .then((res: any) => apply(res.data))
      .catch((error) => toast.error(supportError(error)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAccount = async () => {
    setSavingAccount(true);
    try {
      const res: any = await AxiosUtils.post(API.supportConfigAccount, {
        nickName: nickName.trim() || undefined,
        avatar: avatar?.fileKey ? avatar.fileKey : null,
      });
      apply(res.data);
      toast.success(config?.ready ? 'Đã lưu tên và ảnh.' : 'Đã khởi tạo tài khoản hỗ trợ.');
    } catch (error) {
      toast.error(supportError(error));
    } finally {
      setSavingAccount(false);
    }
  };

  const save = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      const res: any = await AxiosUtils.put(API.supportConfig, {
        welcomeMessage: v.welcomeMessage?.trim() || null,
        workDays: [...v.workDays].sort((a, b) => a - b).join(','),
        workStart: v.workStart.format('HH:mm'),
        workEnd: v.workEnd.format('HH:mm'),
        autoReplyEnabled: v.autoReplyEnabled,
        autoReplyMessage: v.autoReplyMessage?.trim() || null,
        autoReplyCooldownMinutes: v.autoReplyCooldownMinutes,
        signAgentName: v.signAgentName,
      });
      apply(res.data);
      toast.success('Đã lưu cài đặt.');
    } catch (error) {
      toast.error(supportError(error));
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const vars = (meta?.templateVariables || []).map((v) => `{${v.key}}`).join(' ');

  return (
    <div>
      <PageTitle title="Cài đặt hỗ trợ" />
      <Row gutter={[16, 16]}>
        <Col xl={9} lg={24} xs={24}>
          <Card title="Tài khoản “HP Media Hỗ trợ”">
            {!config.ready && (
              <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message="Chưa khởi tạo"
                description="Bấm Khởi tạo một lần. Người dùng sẽ thấy tài khoản này trong mục Tin nhắn của app."
              />
            )}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <PersonAvatar name={nickName} avatar={config.account?.avatar} size={56} />
              <div>
                <Typography.Text strong>{config.account?.nickName || nickName}</Typography.Text>
                <div style={{ fontSize: 12, color: '#9A8F9E' }}>
                  {config.account ? `Tài khoản #${config.account.id}` : 'chưa có'}
                </div>
              </div>
            </div>
            <Form layout="vertical">
              <Form.Item
                label="Tên hiển thị trong app"
                extra="App đặt tên cuộc trò chuyện theo username, nên tên này cũng là username của tài khoản hỗ trợ — không được trùng username của ai khác."
              >
                <Input value={nickName} onChange={(e) => setNickName(e.target.value)} maxLength={60} />
              </Form.Item>
              <Form.Item label="Ảnh đại diện" extra="Nên dùng logo HP Media (PNG/JPG vuông).">
                <FileUpload
                  type={UPLOAD_TYPE.IMAGE}
                  value={avatar as any}
                  maxFileSize={5}
                  onChange={(v: any) => {
                    if (v?.error) toast.error(v.error);
                    setAvatar(v?.fileKey ? { fileKey: v.fileKey, fileUrl: v.fileUrl } : undefined);
                  }}
                />
              </Form.Item>
              <Button type="primary" onClick={saveAccount} loading={savingAccount}>
                {config.ready ? 'Lưu tên & ảnh' : 'Khởi tạo tài khoản hỗ trợ'}
              </Button>
            </Form>
            {config.account && (
              <Descriptions size="small" column={1} style={{ marginTop: 16 }}>
                <Descriptions.Item label="Đăng nhập">
                  {config.account.loginLocked ? 'Đã khoá — không ai đăng nhập được, kể cả Admin' : 'Chưa khoá'}
                </Descriptions.Item>
                <Descriptions.Item label="Vai trò">
                  MOD (lớp Quản lý tổng) — mọi người dùng trả lời được ở mọi chế độ nhắn tin
                </Descriptions.Item>
              </Descriptions>
            )}
          </Card>
        </Col>

        <Col xl={15} lg={24} xs={24}>
          <Card
            title="Tin chào, giờ trực và tin tự động"
            extra={
              <Button type="primary" onClick={save} loading={saving}>
                Lưu cài đặt
              </Button>
            }
          >
            <Form form={form} layout="vertical">
              <Form.Item
                name="welcomeMessage"
                label="Tin chào khi mở kênh cho một người"
                extra={`Biến: ${vars}`}
              >
                <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} maxLength={4000} />
              </Form.Item>
              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Form.Item
                    name="workDays"
                    label="Ngày trực"
                    rules={[{ required: true, type: 'array', min: 1, message: 'Chọn ít nhất một ngày.' }]}
                  >
                    <Checkbox.Group options={DAYS} />
                  </Form.Item>
                </Col>
                <Col md={6} xs={12}>
                  <Form.Item name="workStart" label="Từ (giờ VN)" rules={[{ required: true }]}>
                    <TimePicker format="HH:mm" minuteStep={5} allowClear={false} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col md={6} xs={12}>
                  <Form.Item
                    name="workEnd"
                    label="Đến"
                    dependencies={['workStart']}
                    rules={[
                      { required: true },
                      ({ getFieldValue }) => ({
                        validator: (_, value?: Dayjs) =>
                          value && getFieldValue('workStart')?.format('HH:mm') === value.format('HH:mm')
                            ? Promise.reject(new Error('Giờ kết thúc phải khác giờ bắt đầu.'))
                            : Promise.resolve(),
                      }),
                    ]}
                    extra="Đến < Từ = ca qua đêm."
                  >
                    <TimePicker format="HH:mm" minuteStep={5} allowClear={false} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="autoReplyEnabled" label="Tự động trả lời ngoài giờ trực" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item
                name="autoReplyMessage"
                label="Nội dung tin tự động"
                extra="Tin tự động không tính là đã trả lời — luồng vẫn nằm ở Chờ trả lời cho người trực ca sau."
              >
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={4000} />
              </Form.Item>
              <Form.Item
                name="autoReplyCooldownMinutes"
                label="Không gửi lại tin tự động cho cùng một người trong (phút)"
              >
                <InputNumber min={0} max={10080} step={30} style={{ width: 200 }} />
              </Form.Item>
              <Form.Item
                name="signAgentName"
                label="Ký tên nhân viên cuối mỗi tin trả lời (vd. “— Lan”)"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Form>
            <Typography.Text type="secondary">Giờ trực hiện tại: {config.workHoursText}</Typography.Text>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SettingsTab;
